import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {map, of, switchMap} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {NotificationsActions} from '@store/notifications/notifications.actions';
import {NotificationsService} from '@store/notifications/notifications.service';
import {ROUTER_NAVIGATED} from '@ngrx/router-store';
import {skipUnless, withLatestFromSelectors} from '@store/common/effects.helpers';
import {NotificationsSelectors} from '@store/notifications/notifications.selectors';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {PatronActions} from '@store/patron/patron.actions';

@Injectable({providedIn: 'root'})
export class NotificationsEffects {

  private readonly actions: Actions = inject(Actions);
  private readonly service: NotificationsService = inject(NotificationsService);

  public loadNotificationsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      //During login, ROUTER_NAVIGATED happens before the patron is loaded and gets filtered.
      //Hence, the added ofType loadAuthPatronSuccess. Further navigation can still refresh the notifications.
      ofType(ROUTER_NAVIGATED, PatronActions.loadAuthPatronSuccess),
      skipUnless(PatronSelectors.Auth.hasPatron),
      map(() => NotificationsActions.loadNotifications())
    )
  );

  public loadNotifications = createEffect(() =>
    this.actions.pipe(
      ofType(NotificationsActions.loadNotifications),
      switchMap(() => this.service.loadUnreadNotifications().pipe(
        map(response => NotificationsActions.loadNotificationsSuccess(response)),
        catchError((error: unknown) => of(NotificationsActions.loadNotificationsFailure(error)))
      ))
    )
  );

  public markNotificationAsRead = createEffect(() =>
    this.actions.pipe(
      ofType(NotificationsActions.markNotificationAsRead),
      switchMap(({notificationId}) => this.service.markNotificationsAsRead([notificationId]).pipe(
        map(() => NotificationsActions.markNotificationAsReadSuccess()),
        catchError((error: unknown) => of(NotificationsActions.markNotificationAsReadFailure(error)))
      ))
    )
  );

  public markAllNotificationsAsRead = createEffect(() =>
    this.actions.pipe(
      ofType(NotificationsActions.markAllNotificationsAsRead),
      withLatestFromSelectors(
        NotificationsSelectors.Notifications.allUnreadNotificationIds
      ),
      switchMap(([, notificationIds]) => this.service.markNotificationsAsRead(notificationIds).pipe(
        map(() => NotificationsActions.markAllNotificationsAsReadSuccess()),
        catchError((error: unknown) => of(NotificationsActions.markAllNotificationsAsReadFailure(error)))
      ))
    )
  );

  public reloadNotificationsOnMarkAsRead = createEffect(() =>
    this.actions.pipe(
      ofType(NotificationsActions.markNotificationAsReadSuccess, NotificationsActions.markAllNotificationsAsReadSuccess),
      map(() => NotificationsActions.loadNotifications())
    )
  );
}
